import React, { createContext, useContext, useEffect, useState } from 'react';

const AnalyticsContext = createContext();

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider = ({ children }) => {
  const [config, setConfig] = useState({
    ga4Id: '',
    clarityId: '',
    searchConsoleId: '',
    analyticsEnabled: false,
    cookieConsent: null
  });

  // Load analytics config from backend
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/content`);
        const data = await response.json();
        if (data.analytics) {
          setConfig(prev => ({
            ...prev,
            ga4Id: data.analytics.ga4Id || '',
            clarityId: data.analytics.clarityId || '',
            searchConsoleId: data.analytics.searchConsoleId || '',
            analyticsEnabled: data.analytics.enabled !== false
          }));
        }
      } catch (err) {
        // Analytics config load failed - use defaults
      }
    };
    loadConfig();
  }, []);

  // Check cookie consent
  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    setConfig(prev => ({ ...prev, cookieConsent: consent }));
  }, []);

  // Initialize GA4
  useEffect(() => {
    if (config.ga4Id && config.cookieConsent === 'accepted' && config.analyticsEnabled) {
      // Load GA4 script
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${config.ga4Id}`;
      document.head.appendChild(script);

      // Initialize gtag
      window.dataLayer = window.dataLayer || [];
      function gtag() { window.dataLayer.push(arguments); }
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', config.ga4Id, {
        send_page_view: true,
        cookie_flags: 'SameSite=None;Secure'
      });

      return () => {
        document.head.removeChild(script);
      };
    }
  }, [config.ga4Id, config.cookieConsent, config.analyticsEnabled]);

  // Initialize Microsoft Clarity
  useEffect(() => {
    if (config.clarityId && config.cookieConsent === 'accepted' && config.analyticsEnabled) {
      // Load Clarity script
      (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
      })(window, document, "clarity", "script", config.clarityId);
    }
  }, [config.clarityId, config.cookieConsent, config.analyticsEnabled]);

  // Track page views
  const trackPageView = (pagePath, pageTitle) => {
    if (window.gtag && config.cookieConsent === 'accepted') {
      window.gtag('event', 'page_view', {
        page_path: pagePath,
        page_title: pageTitle
      });
    }
  };

  // Track custom events
  const trackEvent = (eventName, eventParams = {}) => {
    if (window.gtag && config.cookieConsent === 'accepted') {
      // Include UTM parameters if available
      const utmParams = getUTMParams();
      window.gtag('event', eventName, {
        ...eventParams,
        ...utmParams
      });
    }
  };

  // Track conversions
  const trackConversion = (conversionName, value = {}) => {
    trackEvent(conversionName, {
      ...value,
      conversion: true
    });
  };

  // Get UTM parameters from URL or storage
  const getUTMParams = () => {
    const stored = sessionStorage.getItem('utm_params');
    if (stored) {
      return JSON.parse(stored);
    }
    return {};
  };

  // Store UTM parameters
  const storeUTMParams = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const utmParams = {};
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(param => {
      const value = urlParams.get(param);
      if (value) {
        utmParams[param] = value;
      }
    });
    if (Object.keys(utmParams).length > 0) {
      sessionStorage.setItem('utm_params', JSON.stringify(utmParams));
    }
    return utmParams;
  };

  // Initialize UTM tracking on mount
  useEffect(() => {
    storeUTMParams();
  }, []);

  const value = {
    config,
    trackPageView,
    trackEvent,
    trackConversion,
    getUTMParams,
    storeUTMParams
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsContext;
